<template>
  <AppHeader />
  <div class="min-h-screen bg-blue-50">
    <!-- Header section with UP colors -->
    <div class="bg-gradient-to-r from-blue-900 to-blue-700 py-8 shadow-md">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col items-center">
          <div class="text-center">
            <h1 class="text-2xl font-bold text-white mb-1">Create Account</h1>
            <p class="text-blue-100">Join Success Boost today</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div class="grid grid-cols-1 max-w-md mx-auto">
        <div class="bg-white rounded-lg shadow transform transition-transform duration-300 hover:-translate-y-1 hover:shadow-md">
          <div class="p-6">
            <form class="space-y-5" @submit.prevent="register">
              <!-- Email Input -->
              <div>
                <label for="email" class="block text-sm font-medium text-gray-700 mb-1 text-left">Email</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span class="material-symbols-outlined text-gray-400">mail</span>
                  </div>
                  <input
                    id="email"
                    v-model="email"
                    type="email"
                    required
                    class="block w-full pl-10 pr-3 py-3 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-900 focus:border-blue-900 placeholder-gray-400 transition duration-200"
                    :class="{'border-red-500': !isValidEmail && email.length > 0, 'border-gray-300': isValidEmail || email.length === 0}"
                    placeholder="u12345678@tuks.co.za"
                    @blur="validateEmail"
                  />
                </div>
                <!-- Email validation error message -->
                <div v-if="!isValidEmail && email.length > 0" class="text-red-500 text-sm mt-1 text-left">
                  <span class="flex items-center">
                    <span class="material-symbols-outlined text-sm mr-1">error</span>
                    Only University of Pretoria emails (u########@tuks.co.za) are allowed
                  </span>
                </div>
                <!-- Email format hint -->
                <div v-else class="text-gray-500 text-xs mt-1 text-left italic">
                  Email format: u########@tuks.co.za 
                </div>
              </div>

              <!-- Password Input -->
              <div>
                <label for="password" class="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span class="material-symbols-outlined text-gray-400">lock</span>
                  </div>
                  <input
                    id="password"
                    v-model="password"
                    :type="showPassword ? 'text' : 'password'"
                    required
                    class="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-900 focus:border-blue-900 placeholder-gray-400 transition duration-200"
                    placeholder="••••••••"
                  />
                  <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button 
                      type="button" 
                      @click="showPassword = !showPassword"
                      class="text-gray-400 hover:text-gray-500 focus:outline-none"
                    >
                      <span class="material-symbols-outlined text-xl">
                        {{ showPassword ? 'visibility_off' : 'visibility' }}
                      </span>
                    </button>
                  </div>
                </div>
                <!-- Password Strength Indicator -->
                <div v-if="password.length > 0" class="mt-2">
                  <div class="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      class="h-full rounded-full transition-all duration-300"
                      :class="{
                        'bg-red-500': passwordStrength === 0,
                        'bg-yellow-400': passwordStrength === 1,
                        'bg-green-500': passwordStrength >= 2
                      }"
                      :style="{ width: passwordStrength * 33.33 + '%' }"
                    ></div>
                  </div>
                  <p class="text-xs mt-1 text-gray-500">
                    Password strength: 
                    <span :class="{
                      'text-red-500': passwordStrength === 0,
                      'text-yellow-600': passwordStrength === 1,
                      'text-green-600': passwordStrength >= 2
                    }">
                      {{ passwordStrengthText }}
                    </span>
                  </p>
                </div>
              </div>

              <!-- Terms Checkbox -->
              <div class="flex items-start justify-between">
                <div class="flex items-start">
                  <div class="flex items-center h-5">
                    <input id="terms" name="terms" type="checkbox" class="focus:ring-blue-900 h-4 w-4 text-blue-900 border-gray-300 rounded" required />
                  </div>
                  <div class="ml-3 text-sm">
                    <label for="terms" class="font-medium text-gray-700">
                      I agree to the 
                      <a href="#" class="text-blue-900 hover:text-yellow-500 transition-colors">Terms</a> and 
                      <a href="#" class="text-blue-900 hover:text-yellow-500 transition-colors">Privacy Policy</a>
                    </label>
                  </div>
                </div>
              </div>

              <!-- Submit Button -->
              <button
                type="submit"
                :disabled="loading || passwordStrength < 0 || !isValidEmail"
                class="w-full flex items-center justify-center py-3 px-4 rounded-lg font-semibold text-white bg-gradient-to-r from-blue-900 to-blue-700 hover:from-blue-800 hover:to-blue-600 transition disabled:opacity-60"
              >
                <svg v-if="loading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ loading ? "Creating account..." : "Get Started" }}
                <span v-if="!loading" class="material-symbols-outlined ml-2">arrow_forward</span>
              </button>

              <div class="relative h-px bg-gray-100 my-6">
                <span class="absolute left-1/2 -translate-x-1/2 -top-3 bg-white px-3 text-xs text-gray-400">OR</span>
              </div>

              <router-link to="/login" class="w-full flex items-center justify-center py-3 px-4 rounded-lg font-semibold text-blue-900 border border-blue-900 bg-white hover:bg-blue-50 transition">
                Back to Login
                <span class="material-symbols-outlined ml-2">login</span>
              </router-link>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue';
import { registerUser } from '@/composables/useAuth';

export default {
  components: {
    AppHeader
  },
  data() {
    return {
      email: '',
      password: '',
      showPassword: false,
      loading: false,
      isValidEmail: true
    };
  },
  computed: {
    passwordStrength() {
      if (this.password.length === 0) return -1;
      if (this.password.length < 6) return 0;
      if (this.password.length < 8) return 1;
      return 2;
    },
    passwordStrengthText() {
      switch(this.passwordStrength) {
        case -1: return 'Not entered';
        case 0: return 'Weak (too short)';
        case 1: return 'Medium';
        case 2: return 'Strong';
        default: return '';
      }
    }
  },
  methods: {
    validateEmail() {
      if (this.email.length === 0) {
        this.isValidEmail = true;
        return;
      }
      
      // Regex pattern for University of Pretoria emails
      // u followed by exactly 8 digits, then @tuks.co.za
      const upEmailPattern = /^u\d{8}@tuks\.co\.za$/i;
      this.isValidEmail = upEmailPattern.test(this.email);
    },
    async register() {
      // Validate email format first
      this.validateEmail();
      if (!this.isValidEmail) {
        this.$store.dispatch('showAlert', { 
          message: "Please use your University of Pretoria email (u########@tuks.co.za)", 
          type: "error" 
        });
        return;
      }

      if (this.passwordStrength < 1) {
        this.$store.dispatch('showAlert', { 
          message: "Please use a stronger password", 
          type: "error" 
        });
        return;
      }

      try {
        this.loading = true;
        await registerUser(this.email, this.password);
        this.$router.push('/profile');
      } catch (error) {
        console.error("Registration error:", error);
        this.$store.dispatch('showAlert', { 
          message: error.message || "Registration failed. Please try again.", 
          type: "error" 
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
/* Add any component-specific styles here if needed */
</style>